import React, { useCallback, useEffect, useState } from "react";
import { Check, RefreshCw, RotateCcw, UserCheck } from "lucide-react";
import { useRole } from "@/context/RoleContext";
import { PageShell } from "@/pages/dashboard/RolePages";
import api, { formatApiError } from "@/lib/api";

const AdminDiagnosticOperations = () => {
  const { role } = useRole();
  const isDirector = role === "superadmin";
  const [tab, setTab] = useState("assignment");
  const [assignmentRows, setAssignmentRows] = useState([]);
  const [reviewRows, setReviewRows] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [selected, setSelected] = useState({});
  const [observations, setObservations] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [assignment, review, workerList] = await Promise.all([
        api.get("/api/admin/diagnostics/assignment-pool"),
        api.get("/api/admin/diagnostics/review"),
        api.get("/api/admin/diagnostics/workers"),
      ]);
      setAssignmentRows(assignment.data);
      setReviewRows(review.data);
      setWorkers(workerList.data);
    } catch (e) { setError(formatApiError(e)); }
  }, []);

  useEffect(() => {
    load();
    const interval = window.setInterval(load, 2000);
    const refresh = () => load();
    window.addEventListener("focus", refresh);
    window.addEventListener("kd9-diagnostics-changed", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("kd9-diagnostics-changed", refresh);
    };
  }, [load]);

  const assign = async (id) => {
    const worker = selected[id];
    if (!worker) return setError("Selecciona un consultor habilitado.");
    setBusy(id);
    try {
      await api.post(`/api/admin/diagnostics/${id}/assign`, { worker_id: worker });
      window.dispatchEvent(new Event("kd9-diagnostics-changed"));
      await load();
    } catch (e) { setError(formatApiError(e)); await load(); }
    finally { setBusy(""); }
  };

  const review = async (row, action) => {
    const observation = (observations[row.id] || "").trim();
    if (action === "correction" && !observation) return setError("La devolución requiere una observación concreta para el consultor.");
    if (!row.review_token || !row.draft_revision) return setError("La versión enviada no tiene token de revisión. Actualiza la cola.");
    setBusy(row.id);
    setError("");
    try {
      await api.post(`/api/admin/diagnostics/${row.id}/review-worker`, {
        action,
        observation: observation || undefined,
        expected_review_token: row.review_token,
        expected_revision: row.draft_revision,
      });
      setObservations(prev => ({...prev, [row.id]: ""}));
      window.dispatchEvent(new Event("kd9-diagnostics-changed"));
      await load();
    } catch (e) {
      const detail=e?.response?.data?.detail;
      if (detail?.code === "REVIEW_STALE_SUBMISSION" || detail?.code === "REVIEW_DECISION_CONFLICT") {
        setError("La versión cambió mientras revisabas. No se guardó ninguna decisión; la cola fue actualizada.");
        await load();
      } else {
        setError(formatApiError(e));
      }
    } finally { setBusy(""); }
  };

  const rows = tab === "assignment" ? assignmentRows : reviewRows;
  return <PageShell
    kicker={isDirector ? "Director · Diagnósticos" : "Administrador · Diagnósticos"}
    title="ASIGNACIÓN Y REVISIÓN"
    desc="Las colas son excluyentes: un diagnóstico aparece en asignación o en revisión, nunca en ambas."
    badge={<button onClick={load} className="kd9-btn-outline p-2 rounded-lg" aria-label="Actualizar"><RefreshCw size={14}/></button>}
  >
    <div className="mb-5 flex flex-wrap gap-2">
      <button onClick={()=>setTab("assignment")} className={tab==="assignment"?"kd9-btn-primary rounded-lg px-4 py-2 text-xs":"kd9-btn-outline rounded-lg px-4 py-2 text-xs"}>Bolsa de asignación ({assignmentRows.length})</button>
      <button onClick={()=>setTab("review")} className={tab==="review"?"kd9-btn-primary rounded-lg px-4 py-2 text-xs":"kd9-btn-outline rounded-lg px-4 py-2 text-xs"}>Revisión de estudios ({reviewRows.length})</button>
    </div>
    {error && <div className="p-3 mb-4 border border-red-800 text-red-300 rounded-lg">{error}</div>}
    {!rows.length && <div className="kd9-card p-8 text-center">{tab==="assignment"?"No hay diagnósticos pagados sin asignar.":"No hay estudios enviados a revisión."}</div>}
    <div className="space-y-4">{rows.map(row=><article key={row.id} className="kd9-card p-5">
      <div className="flex flex-wrap justify-between gap-3"><div><h3 className="font-cinzel kd9-gold-text text-lg">{row.catalog_title||row.catalog_slug}</h3><div className="text-xs">{row.user_email} · #{row.id.slice(-8)}</div></div><span className="text-[10px] uppercase tracking-widest">{tab==="assignment"?"Pagado sin asignar":"En revisión"}</span></div>
      <details className="mt-4 p-4 border rounded-xl" style={{borderColor:"var(--kd9-border)"}}><summary className="cursor-pointer font-semibold">Consulta, respuestas y material</summary>{row.consultation&&<p className="mt-3">{row.consultation}</p>}{row.answers&&<pre className="mt-3 whitespace-pre-wrap text-xs">{JSON.stringify(row.answers,null,2)}</pre>}{(row.materials||[]).length>0&&<div className="mt-3 flex flex-wrap gap-2">{row.materials.map(file=><a key={file.id} href={file.url} target="_blank" rel="noreferrer" className="kd9-btn-outline px-3 py-2 rounded-lg text-xs">{file.label}: {file.filename}</a>)}</div>}</details>
      {tab==="assignment" ? <div className="mt-4 flex flex-col md:flex-row gap-3"><select className="kd9-input p-3 flex-1" value={selected[row.id]||""} onChange={e=>setSelected({...selected,[row.id]:e.target.value})}><option value="">Seleccionar consultor</option>{workers.map(w=><option key={w.id} value={w.id}>{w.name} · {w.calls_allowed?"Consultor Premium":"Consultor"}</option>)}</select><button disabled={Boolean(busy)} onClick={()=>assign(row.id)} className="kd9-btn-primary px-4 py-2 rounded-lg flex items-center justify-center gap-2"><UserCheck size={14}/> Asignar una vez</button></div>
      : <section className="mt-4 p-4 border rounded-xl" style={{borderColor:"var(--kd9-border)"}}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-widest">Estudio completo para revisión</div>
            <div className="text-[10px] uppercase tracking-widest opacity-60">Versión {row.draft_revision} · Correcciones previas {row.correction_round||0}</div>
          </div>
          <div className="mt-3 whitespace-pre-wrap font-cormorant text-lg">{row.study_content}</div>
          {row.worker_notes&&<p className="mt-3 text-sm"><strong>Nota interna:</strong> {row.worker_notes}</p>}
          {(row.review_history||[]).length>0&&<details className="mt-4 p-3 border rounded-lg" style={{borderColor:"var(--kd9-border)"}}>
            <summary className="cursor-pointer text-xs font-semibold">Historial de revisión ({row.review_history.length})</summary>
            <div className="mt-3 space-y-2">{row.review_history.map((item,index)=><div key={index} className="text-xs opacity-80">
              <strong>{item.action==="correction"?"Devuelto":"Aprobado"}</strong> · versión {item.submission_revision} · ronda {item.correction_round||0}
              {item.observation&&<div className="mt-1 whitespace-pre-wrap">{item.observation}</div>}
            </div>)}</div>
          </details>}
          <textarea
            className="kd9-input mt-4 p-3 min-h-[90px] w-full"
            placeholder="Observación de revisión. Es obligatoria si devuelves el diagnóstico."
            value={observations[row.id]||""}
            onChange={e=>setObservations(prev=>({...prev,[row.id]:e.target.value}))}
          />
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <button disabled={busy===row.id} onClick={()=>review(row,"approve")} className="kd9-btn-primary px-4 py-2 rounded-lg flex gap-2 justify-center items-center"><Check size={14}/> Aprobar revisión</button>
            <button disabled={busy===row.id} onClick={()=>review(row,"correction")} className="kd9-btn-outline px-4 py-2 rounded-lg flex gap-2 justify-center items-center"><RotateCcw size={14}/> Devolver al consultor</button>
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-widest opacity-60">Aprobar revisión no entrega al usuario: pasa a la cola de Entregas finales.</p>
        </section>}
    </article>)}</div>
  </PageShell>;
};
export default AdminDiagnosticOperations;
